import { RegulatoryChange, DATA_API_URL } from '../lib/api';

export interface PolicyUpdate {
    id: string;
    title: string;
    summary: string;
    department: 'Billing' | 'Finance' | 'Clinical' | 'Compliance';
    urgency: 'High' | 'Medium' | 'Low';
    timestamp: string;
    impact: string;
    status: 'New' | 'In Review' | 'Implemented';
    effectiveDate: string;
    financialImpact: string;
    affectedDepartments: string[];
    detailedChanges: string[];
    actionItems: Array<{
        id: string;
        description: string;
        dueDate: string;
        priority: 'High' | 'Medium' | 'Low';
        assignedDepartment: string;
    }>;
    complianceChecklist: Array<{
        id: string;
        task: string;
        dueDate: string;
        completed: boolean;
    }>;
    sourceDocuments: Array<{
        title: string;
        url: string;
        type: string;
    }>;
    // Original data for reference if needed
    originalData?: any;
    qualityScore?: number;
    qualityGrade?: string;
    safeToUse?: boolean;
}

// Detect if a document is from the archive API (older structure)
export const isArchivedDocument = (doc: any): boolean => {
    if (!doc) return false;
    return (
        doc.source_citations !== undefined ||
        doc.document_id !== undefined ||
        doc.quality_control !== undefined ||
        typeof doc.id === 'number'
    );
};

// Resolve a usable PDF URL for any document type
export const getPDFUrl = (doc: any): string | null => {
    if (!doc) return null;
    if (isArchivedDocument(doc)) {
        return (
            doc.document_info?.pdf_url ||
            doc.pdf_url ||
            doc.s3_url ||
            doc.document_url ||
            doc.url ||
            null
        );
    }
    // Recent documents (live API)
    return (
        doc.document_info?.pdf_url ||
        doc.document_url ||
        doc.pdf_link ||
        doc.pdf_url ||
        doc.source_pdf ||
        doc.s3_url ||
        doc.url ||
        doc?.sourceDocuments?.[0]?.url ||
        null
    );
};

// Normalize citations for archive docs (embedded)
export const getArchivedDocumentCitations = (document: any) => {
    const sourceCitations = document?.source_citations ||
        document?.simplified_analysis?.source_citations ||
        [];
    if (!Array.isArray(sourceCitations)) return [];
    return sourceCitations.map((citation: any, index: number) => ({
        id: index,
        text: citation.supporting_text || citation.claim || '',
        page: citation.page_number || 1,
        confidence: citation.confidence_score || 0,
        category: citation.claim_type || 'general',
        claim: citation.claim || '',
        context: citation.context || '',
        section: citation.section || '',
    }));
};

// Normalize citations for live docs (fetched)
export const getRecentDocumentCitations = async (
    documentId: string | number,
    dataApiUrl: string = DATA_API_URL
) => {
    try {
        console.log('📋 FETCHING citations for RECENT document:', documentId);
        const response = await fetch(`${dataApiUrl}/api/changes/${documentId}/citations`);
        if (!response.ok) {
            console.warn(`⚠️ Citations API returned ${response.status} for document ${documentId}`);
            return [];
        }
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data.citations || data || []);
        if (!Array.isArray(list)) {
            console.warn('⚠️ Citations response is not an array:', list);
            return [];
        }
        return list.map((citation: any, index: number) => ({
            id: citation.id ?? index,
            text: citation.text || citation.supporting_text || citation.extracted_text || '',
            page: citation.page || citation.page_number || 1,
            confidence: citation.confidence || citation.confidence_score || 0,
            category: citation.category || citation.claim_type || 'general',
            claim: citation.claim || citation.text || '',
            context: citation.context || '',
        }));
    } catch (err) {
        console.error('❌ Failed to fetch recent citations:', err);
        return [];
    }
};

// Universal citation getter
export const getCitations = async (
    document: any,
    dataApiUrl: string = DATA_API_URL
): Promise<any[]> => {
    if (isArchivedDocument(document)) {
        return getArchivedDocumentCitations(document);
    }
    const documentId = document?.id ?? document?.document_id;
    if (!documentId) return [];
    return await getRecentDocumentCitations(documentId, dataApiUrl);
};

const TITLE_PREFIXES_TO_STRIP = [
    'CMS Manual System  Department of Health &',
    'CMS Manual System Department of Health &',
    'CMS Manual System Department of Health and Human Services',
    'CMS Manual System',
    'Department of Health & Human Services',
    'Department of Health and Human Services',
    'Centers for Medicare & Medicaid Services',
    'Centers for Medicare and Medicaid Services',
    'Centers for Disease Control and Prevention',
    'Food and Drug Administration',
    'CDC -',
    'FDA -',
    'CMS -',
];

export const getDocumentTitle = (doc: any): string => {
    const analysis = doc?.simplified_analysis || {};
    if (analysis.one_sentence_summary) {
        return analysis.one_sentence_summary;
    }
    if (analysis.paragraph_summary) {
        const firstSentence = analysis.paragraph_summary.split(/[.!?]/)[0];
        if (firstSentence && firstSentence.length > 20) {
            return firstSentence.trim();
        }
    }

    if (doc?.title) {
        let title = String(doc.title).trim();
        for (const prefix of TITLE_PREFIXES_TO_STRIP) {
            if (title.startsWith(prefix)) {
                title = title.substring(prefix.length).trim();
                break;
            }
        }
        title = title.replace(/^[-:]\s*/, '');
        if (title.length >= 15 && !title.endsWith('&')) {
            return title;
        }
    }
    return analysis.one_sentence_summary || 'Untitled Document';
};

/**
 * Normalize document data from various API endpoints
 * This ensures consistent structure regardless of source
 */
export const normalizeDocument = (change: RegulatoryChange | any): PolicyUpdate => {
    if (!change) {
        return {
            id: '0',
            title: 'Untitled',
            summary: 'No summary available',
            department: 'Compliance',
            urgency: 'Medium',
            timestamp: 'Recently',
            impact: 'No impact available',
            status: 'New',
            effectiveDate: 'To be determined',
            financialImpact: 'Assessment pending',
            affectedDepartments: [],
            detailedChanges: [],
            actionItems: [],
            complianceChecklist: [],
            sourceDocuments: [],
        };
    }

    // PRIORITY: Use flattened structure first (new API format), fall back to nested (old format)
    // Handle both RegulatoryChange type and raw API response
    const safeAnalysis = change.simplified_analysis || change.primary_analysis || {
        detailed_summary: '',
        one_sentence_summary: '',
        paragraph_summary: '',
        who_this_affects: { healthcare_roles: [], facility_types: [], departments: [] },
        next_steps: [],
        key_requirements: [],
        detailed_changes: [],
        source_link: '',
    };

    const safeQuality = change.quality_control || null;

    // Prioritize flattened affected_departments over nested
    const safeAffectedDepts = change.affected_departments ||
        safeAnalysis.who_this_affects?.departments ||
        [];

    // Map department based on affected departments
    const getDepartment = (): 'Billing' | 'Finance' | 'Clinical' | 'Compliance' => {
        const depts = (safeAffectedDepts || []).map((d: string) => d.toLowerCase());
        if (depts.includes('billing')) return 'Billing';
        if (depts.includes('finance')) return 'Finance';
        if (depts.includes('clinical')) return 'Clinical';
        return 'Compliance';
    };

    // Map urgency based on quality score or compliance deadline
    const getUrgency = (): 'High' | 'Medium' | 'Low' => {
        // Prioritize flattened urgency_level/priority
        if (change.urgency_level) {
            const level = change.urgency_level.toLowerCase();
            if (level.includes('high') || level.includes('urgent')) return 'High';
            if (level.includes('low')) return 'Low';
            return 'Medium';
        }
        if (change.priority) {
            const priority = change.priority.toLowerCase();
            if (priority === 'high') return 'High';
            if (priority === 'low') return 'Low';
            return 'Medium';
        }

        if (safeAnalysis.compliance_deadline) {
            const deadline = new Date(safeAnalysis.compliance_deadline);
            const now = new Date();
            const daysUntil = Math.floor((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

            if (daysUntil < 30) return 'High';
            if (daysUntil < 90) return 'Medium';
        }

        if ((safeAnalysis.next_steps || []).some((step: any) => step.priority === 'high')) {
            return 'High';
        }

        return 'Medium';
    };

    // Determine status based on quality control
    const getStatus = (): 'New' | 'In Review' | 'Implemented' => {
        if (!safeQuality) return 'New';

        // Prioritize flattened quality_score
        const qualityScore = change.quality_score || safeQuality.quality_score || 0;
        const safeToUse = change.safe_to_use !== undefined ? change.safe_to_use : safeQuality.safe_to_use;

        if (safeToUse && qualityScore >= 80) {
            return 'Implemented';
        }
        return 'In Review';
    };

    // Calculate relative timestamp
    const getRelativeTime = (): string => {
        // Prioritize flattened date fields
        const dateStr = change.date ||
            change.published_date ||
            change.analysis_timestamp ||
            change.discovered_date;

        if (!dateStr) return 'Recently';

        const now = new Date();
        const timestamp = new Date(dateStr);

        // Check if date is valid
        if (isNaN(timestamp.getTime())) return 'Recently';

        const diffMs = now.getTime() - timestamp.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffHours < 1) return 'Just now';
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    };

    // Prioritize flattened id over nested document_id
    const docId = (change.id ?? change.document_id ?? change.title ?? '0').toString();
    const normalizedTitle = getDocumentTitle(change);

    return {
        id: docId,
        title: normalizedTitle || `Document ${change.document_id ?? change.id ?? ''}` || 'Untitled',

        // Prioritize flattened summary over nested
        summary: change.summary ||
            safeAnalysis.paragraph_summary ||
            safeAnalysis.one_sentence_summary ||
            (safeAnalysis.detailed_summary
                ? `${safeAnalysis.detailed_summary.substring(0, 200)}...`
                : 'No summary available'),

        department: getDepartment(),
        urgency: getUrgency(),
        timestamp: getRelativeTime(),
        impact: safeAnalysis.financial_impact
            ? `Financial impact: ${safeAnalysis.financial_impact.estimated_cost || 'To be determined'}`
            : 'Operational impact assessment available in source documents',
        status: getStatus(),

        // Prioritize flattened date
        effectiveDate: change.date ||
            change.published_date ||
            safeAnalysis.compliance_deadline ||
            'To be determined',

        financialImpact: change.financial_impact ||
            safeAnalysis.financial_impact?.estimated_cost ||
            'Assessment pending',

        affectedDepartments: safeAffectedDepts,

        // Prioritize flattened detailed_changes
        detailedChanges: change.detailed_changes ||
            safeAnalysis.detailed_changes ||
            safeAnalysis.key_requirements ||
            [],

        actionItems: (safeAnalysis.next_steps || []).map((step: any, idx: number) => ({
            id: `a${docId}-${idx}`,
            description: step.action,
            dueDate: step.timeline,
            priority: (step.priority
                ? (step.priority.charAt(0).toUpperCase() + step.priority.slice(1).toLowerCase())
                : 'Medium') as 'High' | 'Medium' | 'Low',
            assignedDepartment: step.owner
        })),
        complianceChecklist: (safeAnalysis.next_steps || []).slice(0, 3).map((step: any, idx: number) => ({
            id: `c${docId}-${idx}`,
            task: step.action,
            dueDate: step.timeline,
            completed: false
        })),
        sourceDocuments: [{
            title: change.title || 'Source document',
            url: change.url || change.s3_url,
            type: change.source || 'Unknown'
        }],
        originalData: change,

        // Prioritize flattened quality fields
        qualityScore: change.quality_score ?? safeQuality?.quality_score ?? 0,
        qualityGrade: change.quality_grade || safeQuality?.quality_grade || 'Unknown',
        safeToUse: change.safe_to_use !== undefined ? change.safe_to_use : (safeQuality?.safe_to_use || false)
    };
};

export const normalizeDocuments = (rawDocs: any[]): PolicyUpdate[] => {
    if (!Array.isArray(rawDocs)) {
        console.warn('⚠️ normalizeDocuments received non-array:', rawDocs);
        return [];
    }
    return rawDocs.map(normalizeDocument);
};
